<template>
  <div>
    <custom-text :msg="customText" :customValue="studentData" class="mt-3"></custom-text>
    <div class="text-xs-center mt-3">
      <div class="graphSelection">
        <v-flex xs12 sm12 px-2>
          <v-layout column class="align-start">
            <v-layout column>
              <v-layout row class="mt-4">
                <date-range-picker :dateRange="dateRange" @updated="newDateRange"></date-range-picker>
              </v-layout>
              <span v-if="dateRangeInvalid" class="caption" style="color: red">{{$t('invalid_date_range')}}</span>
              <v-layout wrap row justify-space-between class="mt-2">
                <v-btn flat class="link" @click="showLast(7)">{{$t('student_graph_last_week')}}</v-btn>
                <div class="button-bar"></div>
                <v-btn flat class="link" @click="showLast(28)">{{$t('student_graph_last_4_weeks')}}</v-btn>
                <div class="button-bar"></div>
                <v-btn flat class="link" @click="showLast(70)">{{$t('student_graph_last_10_weeks')}}</v-btn>
              </v-layout>
            </v-layout>
          </v-layout>
        </v-flex>

        <v-flex xs12 sm12 px-2>
            <v-layout row class="mt-4 row-column-align-center">
              <v-flex v-if="!flagEngagementOverallButton">
                <v-btn @click="graphMode('weekByWeek')" color="primary" class="showCase primary-text"
                      :outline="engagementGraphMode != 'weekByWeek'">
                  {{$t('student_engagement_daily')}}
                </v-btn>
                <v-btn @click="graphMode('overall')" color="primary" class="showCase primary-text"
                      :outline="engagementGraphMode != 'overall'">
                  {{$t('student_engagement_overall')}}
                </v-btn>
              </v-flex>
            </v-layout>
        </v-flex>

        <v-flex xs12 sm12 px-2>
          <v-layout row class="mt-4 row-column-align-right">
            <div>
              <v-layout row wrap>
                <v-flex v-if="flagInterventionCheckbox">
                  <v-checkbox color="purple" :label="$t('student_graph_course_interventions')"
                              v-model="graphInterventions"
                  />
                  <div class="check-box-size">{{$t('student_graph_course_interventions')}}</div>
                </v-flex>
                <v-flex v-if="flagAlertCheckbox">
                  <v-checkbox color="purple" :label="$t('student_graph_course_alerts')" v-model="graphAlerts"
                  />
                  <div class="check-box-size">{{$t('student_graph_course_alerts')}}</div>
                </v-flex>
                <v-flex>
                  <v-checkbox color="purple" :label="$t('student_graph_course_change')" v-model="graphCourseChange"
                  />
                  <div class="check-box-size">{{$t('student_graph_course_change')}}</div>
                </v-flex>
                <v-flex>
                  <v-checkbox :label="$t('student_graph_course_average')" v-model="graphCourseAverage"
                              class="graphCourseAverage" color="red"/>
                  <div class="check-box-size">{{$t('student_graph_course_average')}}</div>
                </v-flex>
                <v-flex v-if="flagDisplayTop25">
                  <v-checkbox :label="$t('student_graph_course_top')" v-model="graphTopPercent"
                              class="graphTopPercent" color="green"/>
                  <div class="check-box-size">{{$t('student_graph_course_top')}}</div>
                </v-flex>
                <v-flex v-if="flagExemptionCheckbox">
                  <v-checkbox :label="$t('student_graph_exemptions')" v-model="getGraphExemptions"
                              class="graphExemptions" color="light-blue lighten-3"/>
                  <div class="check-box-size">{{$t('student_graph_exemptions')}}</div>
                </v-flex>
                <v-flex class="vertical-bar">
                </v-flex>
              </v-layout>
            </div>
          </v-layout>
        </v-flex>
      </div>
    </div>

    <div style="width: 100%;">
      <EngagementGraph
        :active="active"
        v-bind:studentId="studentId"
        v-bind:courseAverage="graphCourseAverage"
        v-bind:topPercent="graphTopPercent"
        v-bind:exemptions="getGraphExemptions"
        v-bind:style="graphStyle"
        v-bind:engagementGraphMode="engagementGraphMode"
        v-bind:dateRange="dateRange">
      </EngagementGraph>
    </div>
    <v-layout row wrap pt-2>
      <v-flex class="summary-graph-width">
        <div class="headline">{{$t('student_engagement_summary_graph_title')}}
          <v-tooltip bottom max-width="300">
            <v-icon slot="activator">info_outline</v-icon>
            <span>{{$t('student_engagement_summary_graph_tooltip')}}</span>
          </v-tooltip>
        </div>
        <engagement-summary-graph :active="active" :engagement="studentEngagementSummary" style="height: 250px;"/>
      </v-flex>
      <v-flex>
        <div class="calendar-container pt-3">
          <student-calendar :ratings="ratings"
                            :color-map="colorMap"
                            :rating-map="ratingMap"/>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
  import EngagementGraph from './EngagementGraph'
  import DateRangePicker from '../../date/DateRangePicker'
  import moment from 'moment-es6'
  import StudentCalendar from './StudentCalendar'
  import CustomText from '../../help/CustomText'
  import { mapGetters, mapState } from 'vuex'
  import {
    GET_STUDENT_CALENDAR_INFO_BY_DATE,
    HAS_FEATURE_FLAG
  } from '../../../store/getter-types'
  import { SET_GRAPH_ALERTS, SET_GRAPH_COURSE_CHANGE, SET_GRAPH_INTERVENTIONS } from '../../../store/mutation-types'
  import EngagementSummaryGraph from './EngagementSummaryGraph'
  import Roles from '../../../mixins/Roles'

  export default {
    name: 'StudentEngagement',
    props: ['studentId', 'active'],
    mixins: [Roles],
    computed: {
      ...mapState({
        colorMap: state => state.config.ratingColors,
        ratingMap: state => state.config.ratingMap,
        studentEngagementSummary: state => state.student.studentEngagementSummary,
        studentData: state => state.student,
        moduleAlert: state => state.profile.module.alert,
        moduleInterExem: state => state.profile.module.interexem,
        moduleEngagement: state => state.profile.module.engagement,
        functionStudentEngagement: state => state.student.functions.studentEngagement,
        optionAlertCheckbox: state => state.student.options.enable.alertCheckbox,
        optionAlertIndicator: state => state.student.options.enable.alertIndicator,
        optionInterventionCheckbox: state => state.student.options.enable.interventionCheckbox,
        optionInterventionIndicator: state => state.student.options.enable.interventionIndicator,
        optionExemptionCheckbox: state => state.student.options.enable.exemptionCheckbox,
        optionExemptionIndicator: state => state.student.options.enable.exemptionIndicator,
        optionAdminTop25: state => state.student.options.enable.adminTop25,
        optionStaffTop25: state => state.student.options.enable.staffTop25,
        optionStudentTop25: state => state.student.options.enable.studentTop25,
        optionEngagementOverallButton: state => state.student.options.disable.engagementOverallButton
      }),
      ...mapGetters({
        ratings: GET_STUDENT_CALENDAR_INFO_BY_DATE,
        hasFeatureFlag: HAS_FEATURE_FLAG
      }),
      getGraphExemptions: {
        get () {
          return this.flagExemptionIndicator ? this.graphExemptions : false
        },
        set (value) {
          this.graphExemptions = this.flagInterventionIndicator ? value : false
        }
      },
      graphInterventions: {
        get () {
          return this.flagInterventionIndicator ? this.$store.state.student.showGraphInterventions : false
        },
        set (value) {
          this.$store.commit(SET_GRAPH_INTERVENTIONS, this.flagInterventionIndicator ? value : false)
        }
      },
      graphAlerts: {
        get () {
          return this.flagAlertIndicator ? this.$store.state.student.showGraphAlerts : false
        },
        set (value) {
          this.$store.commit(SET_GRAPH_ALERTS, this.flagAlertIndicator ? value : false)
        }
      },
      graphCourseChange: {
        get () {
          return this.$store.state.student.showGraphCourseChange
        },
        set (value) {
          this.$store.commit(SET_GRAPH_COURSE_CHANGE, value)
        }
      },
      flagDisplayTop25 () {
        let option = this.isAdmin ? this.optionAdminTop25 : (this.isStaff ? this.optionStaffTop25 : this.optionStudentTop25)
        return this.hasFeatureFlag(this.moduleEngagement + '.' + this.functionStudentEngagement + '.' + option)
      },
      customText () {
        let role = this.isAdmin ? 'admin' : (this.isStaff ? 'staff' : 'student')
        return 'custom-text.' + role + '.engagement'
      },
      flagAlertCheckbox () {
        return this.hasFeatureFlag(this.moduleAlert + '.' + this.functionStudentEngagement + '.' + this.optionAlertCheckbox)
      },
      flagAlertIndicator () {
        return this.hasFeatureFlag(this.moduleAlert + '.' + this.functionStudentEngagement + '.' + this.optionAlertIndicator)
      },
      flagInterventionCheckbox () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionStudentEngagement + '.' + this.optionInterventionCheckbox)
      },
      flagInterventionIndicator () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionStudentEngagement + '.' + this.optionInterventionIndicator)
      },
      flagExemptionCheckbox () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionStudentEngagement + '.' + this.optionExemptionCheckbox)
      },
      flagExemptionIndicator () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionStudentEngagement + '.' + this.optionExemptionIndicator)
      },
      flagEngagementOverallButton () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionStudentEngagement + '.' + this.optionEngagementOverallButton)
      }
    },
    data () {
      return {
        dateRangeInvalid: false,
        engagementGraphMode: 'weekByWeek',
        graphCourseAverage: false,
        graphTopPercent: false,
        graphExemptions: true,
        graphStyle: {
          height: '40vh',
          'min-height': '200px',
          position: 'relative'
        },
        dateRange: this.getDateRange(28)
      }
    },
    methods: {
      graphMode (mode) {
        this.engagementGraphMode = mode
      },
      getDateRange (nDays) {
        let format = 'YYYY-MM-DD'
        return {
          startDate: moment().subtract(nDays, 'days').format(format),
          endDate: moment().subtract(1, 'days').format(format)
        }
      },
      showLast (nDays) {
        this.dateRange = this.getDateRange(nDays)
      },
      newDateRange (newRange) {
        let diff = moment(newRange.endDate).diff(moment(newRange.startDate), 'days')
        if (diff >= 0) {
          this.dateRangeInvalid = false
          this.dateRange = newRange
        } else {
          this.dateRangeInvalid = true
        }
      }
    },
    components: {
      EngagementSummaryGraph,
      StudentCalendar,
      EngagementGraph,
      DateRangePicker,
      CustomText
    },
    mounted () {
      this.graphAlerts = true
      this.graphInterventions = true
      this.graphCourseChange = true
    }
  }
</script>

<style scoped>

  .summary-graph-width {
    width: calc(100% - 280px);
  }

  @media only screen and (max-width: 960px) {
    .summary-graph-width {
      width: 100%;
    }
  }

  .calendar-container {
    display: flex;
    justify-content: flex-end;
  }

  @media only screen and (max-width: 599px) {
    .calendar-container {
      justify-content: center;
    }
  }

  .button-bar {
    display: inline-block;
    height: 28px;
    border-right: 2px solid lightgray;
    margin: 12px 0px 0px 0px;
  }

  /* Required because the min size of the checkbox will
     put ellipsis on the the label text */
  .check-box-size {
    font-size: 20px;
    padding-left: 30px;
    padding-right: 6px;
    height: 0px;
    overflow: hidden;
  }

  button.link.btn {
    text-decoration: underline;
    text-transform: none;
  }

  .showCase {
    text-transform: none;
    width: 100%;
    max-width: 150px;
  }

  @media (min-width: 600px) and (max-width: 1200px) {
    .graphSelection .link {
      font-size: 11px;
    }
  }

  @media (max-width: 1200px) {
    .graphSelection .link {
      min-width: 63px;
      font-size: 10px;
    }
    .graphSelection .checkbox  {
      flex-wrap:nowrap;
    }
  }
</style>

<style>
  .graphSelection {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .graphSelection .link {
    margin: 0px;
  }

  .graphSelection .link .btn__content {
    padding-left: 8px;
    padding-right: 8px;
  }

  .row-column-align-left,
  .row-column-align-right {
    justify-content: center;
  }

  /* Tablet sm */
  @media (min-width: 600px) {
    .graphSelection {
      flex-direction: row;
      justify-content: space-between;
    }

    .row-column-align-left {
      justify-content: flex-start;
    }

    .row-column-align-right {
      justify-content: flex-end;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/student/engagement/StudentEngagement.vue